import { FormEvent, useContext, useState } from "react";
import ApiErrorMessages from "../UI/ApiErrorMessages";
import Button from "../UI/Button";
import Input from "../UI/Input";
import Loader from "../UI/Loader";
import Modal from "../UI/Modal";
import Client from "../../models/serviceModels/Client";
import { searchClients } from "../../services/clientsService";
import { attachPublicSubmission } from "../../services/formsService";
import { formatOptionalJSONDateWithoutTime } from "../../utils/dateHelpers";
import './AttachPublicSubmissionModal.css';
import { OrganizationContext } from '../../App';
import { isDemographicVisible } from '../../utils/demographicsSchema';

interface AttachPublicSubmissionModalProps {
    visible: boolean;
    formInstanceId: string;
    onClose: () => void;
    onAttached: (client: { id: string; firstName: string; lastName: string }) => void;
}

const AttachPublicSubmissionModal = (props: AttachPublicSubmissionModalProps) => {
    const organization = useContext(OrganizationContext);
    const showEmail = isDemographicVisible(organization.value, 'email');
    const showPhone = isDemographicVisible(organization.value, 'phone');
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<Client[]>([]);
    const [selected, setSelected] = useState<Client>();
    const [searching, setSearching] = useState(false);
    const [attaching, setAttaching] = useState(false);
    const [searched, setSearched] = useState(false);
    const [apiError, setApiError] = useState<any>();

    const close = () => {
        if (attaching) {
            return;
        }
        setQuery('');
        setResults([]);
        setSelected(undefined);
        setSearched(false);
        setApiError(undefined);
        props.onClose();
    };

    const search = (event: FormEvent) => {
        event.preventDefault();
        const normalizedQuery = query.trim();
        if (!normalizedQuery) {
            return;
        }

        setSearching(true);
        setSearched(true);
        setSelected(undefined);
        setApiError(undefined);
        searchClients({
            query: normalizedQuery,
            skip: 0,
            take: 10,
            includeDeactivated: false
        }).then(response => {
            setResults(response.data.filter(client => !client.deactivated));
        }).catch(error => {
            setApiError(error);
            setResults([]);
        }).finally(() => {
            setSearching(false);
        });
    };

    const attach = () => {
        if (!selected) {
            return;
        }

        setAttaching(true);
        setApiError(undefined);
        attachPublicSubmission(props.formInstanceId, selected.id).then(response => {
            props.onAttached({
                id: response.clientId,
                firstName: response.firstName,
                lastName: response.lastName
            });
            setQuery('');
            setResults([]);
            setSelected(undefined);
            setSearched(false);
            props.onClose();
        }).catch(error => {
            setApiError(error);
        }).finally(() => {
            setAttaching(false);
        });
    };

    return <Modal id="attach-public-submission" visible={props.visible} className="medium" title="Attach to Existing Participant" onClose={close}>
        <p>This associates the submission with a participant. It will not change the participant's profile or copy answers into their Additional Information.</p>
        <ApiErrorMessages error={apiError} />
        <form onSubmit={search}>
            <div className="row align-items-end">
                <div className="column">
                    <Input
                        id="attach-participant-query"
                        label="Search Participants"
                        placeholder="Name, email, phone number, or identifier"
                        value={query}
                        onChange={setQuery} />
                </div>
                <div className="column-auto">
                    <Button id="search-attach-participants" text="Search" type="submit" loading={searching} disabled={searching || !query.trim()} />
                </div>
            </div>
        </form>
        {searching && <Loader />}
        {!searching && searched && results.length === 0 && <p>No active participants found.</p>}
        {!searching && results.length > 0 && <fieldset className="attach-participant-results">
            <legend>Select one participant</legend>
            {results.map(client => <label key={client.id} className={selected?.id === client.id ? 'attach-participant-result selected' : 'attach-participant-result'}>
                <input
                    type="radio"
                    name="attach-participant"
                    value={client.id}
                    checked={selected?.id === client.id}
                    onChange={() => setSelected(client)} />
                <span>
                    <span className="text-bold">{client.firstName} {client.middleName} {client.lastName}</span>
                    {client.dateOfBirth && <span>Date of birth: {formatOptionalJSONDateWithoutTime(client.dateOfBirth, 'MM/dd/yyyy')}</span>}
                    {showEmail && client.email && <span>{client.email}</span>}
                    {showPhone && client.phoneNumber && <span>{client.phoneNumber}</span>}
                </span>
                <a href={`/participants/${client.id}/profile-demographics`} target="_blank" rel="noreferrer">Open Profile</a>
            </label>)}
        </fieldset>}
        {selected && <div className="attach-participant-confirmation" role="status">
            Attach this submission to <strong>{selected.firstName} {selected.lastName}</strong>? Their profile will not be changed.
        </div>}
        <div className="row justify-content-between margin-top-5">
            <div className="column-auto"><Button id="cancel-attach-submission" text="Cancel" className="secondary" disabled={attaching} onClick={close} /></div>
            <div className="column-auto"><Button id="confirm-attach-submission" text="Attach Submission" loading={attaching} disabled={attaching || !selected} onClick={attach} /></div>
        </div>
    </Modal>;
};

export default AttachPublicSubmissionModal;
